/**
 * @copyright Copyright 2022-2025 Epic Systems Corporation
 * @file Organization Management data
 * @module Epic.AppOrchard.Data
 */

import { IRecordList } from "ao/components/ConnectorManagement/data/data";
import { IFilterDataAsyncResponse, ISelectionOption, SelectOptionType } from "ao/types";
import {
	IAddArchiveRequestModel,
	IConnectorsOrganizationFormModel,
	IConnectorsOrganizationListItem,
	IOrganizationAgreementModel,
	IOrganizationArchiveInfo,
	IOrganizationArchiveRequest,
	IOrganizationArchivingModel,
	IOrganizationEnrollmentFormModel,
	IOrganizationEnrollmentModel,
	IOrganizationFormLoadedModel,
	IOrganizationFormModel,
	IOrganizationManagementModel,
	IOrganizationSaveForConnectorsResponse,
	IOrganizationSaveResponse,
	ISaveDefaultColumnsRequest,
	ISaveResearchRequest,
	ISaveUserWorkflowRequest,
	IUserManagementModel,
	IUserModel,
	IUserOrganizationUpdate,
	IUserRemoteAssociations,
} from "ao/types/organizationManagement";
import api, { fileApi } from "ao/utils/api";
import { getDatePart } from "ao/utils/helpers";

export const StaffLocationMetadataItemId = 64;
export const ParentAccountMetadataItemId = 68;

/**
 * Load Organization Management page model
 * @returns IOrganizationModel array
 */
export async function loadOrganizationManagement(
	listParameterRecords: Record<string, string>,
): Promise<IOrganizationManagementModel> {
	const data: IOrganizationManagementModel = await api.get<IOrganizationManagementModel>(
		"/api/OrganizationManagement/List",
		listParameterRecords,
	);
	return data;
}

/**
 * Load filtered list of Connectors Organizations.
 * Accepts query parameters for filtering, sorting, and pagination.
 *
 * @returns Filtered org list with total count
 */
export async function loadOrganizationManagementForConnectors(
	listParameterRecords: Record<string, string>,
): Promise<IRecordList> {
	const data = await api.get<{
		organizationList: IConnectorsOrganizationListItem[];
		totalCount: number;
	}>("/api/OrganizationManagement/ListForConnectors", listParameterRecords);

	return {
		recordList: data.organizationList,
		recordTotal: data.totalCount,
	};
}

export async function getFilteredRecordsExport(listParameterRecords: Record<string, string>): Promise<void> {
	await fileApi.get<void>(
		`/api/OrganizationManagement/ExportRecords`,
		"text/csv",
		`OrganizationManagement_${getDatePart(new Date())}.csv`,
		listParameterRecords,
	);
	return;
}

export async function getFilteredRecordsExportForConnectors(
	listParameterRecords: Record<string, string>,
): Promise<void> {
	await fileApi.get<void>(
		"/api/OrganizationManagement/ExportRecordsForConnectors",
		"text/csv",
		`ConnectorsOrganizations_${getDatePart(new Date())}.csv`,
		listParameterRecords,
	);
}

/**
 * Load Organization Details page model
 * @returns IOrganizationManagementModel
 */
export async function loadOrganizationDetails(id?: number): Promise<IOrganizationFormLoadedModel> {
	const data: IOrganizationFormLoadedModel = await api.get<IOrganizationFormLoadedModel>(
		`/api/OrganizationManagement/Load/${id}`,
	);
	return data;
}

/**
 * Load Organization for Connectors Organization Management page
 * @returns IOrganizationFormModel
 */
export async function loadOrganizationDetailsForConnectors(
	id?: number,
): Promise<IConnectorsOrganizationFormModel> {
	return await api.get<IConnectorsOrganizationFormModel>(
		`/api/OrganizationManagement/LoadForConnectors/${id}`,
	);
}

/**
 * Post Organization form
 * @param orgModel organization data to post
 * @returns IOrganizationResponse
 */
export async function saveOrganization(orgModel: IOrganizationFormModel): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		"/api/OrganizationManagement/Save",
		orgModel,
	);
	return data;
}

/**
 * Post Connectors Organization form
 * @param orgModel organization data to post
 * @returns IOrganizationResponse
 */
export async function saveOrganizationForConnectors(
	orgModel: IConnectorsOrganizationFormModel,
): Promise<IOrganizationSaveForConnectorsResponse> {
	return await api.post<IOrganizationSaveForConnectorsResponse>(
		"/api/OrganizationManagement/SaveForConnectors",
		orgModel,
	);
}

/**
 * Lookup organization name by SLG
 * @returns Organization name as string
 */
export async function lookupOrganizationBySLG(slg: number): Promise<string> {
	const data: string = await api.get<string>(`/api/OrganizationManagement/Lookup?slgId=${slg}`);
	return data;
}

/**
 * Load User Management page model
 * @returns IUserModel array
 */
export async function loadOrganizationUsers(
	listParameterRecords: Record<string, string>,
): Promise<IUserManagementModel> {
	const data: IUserManagementModel = await api.get<IUserManagementModel>(
		"/api/OrganizationManagement/Users",
		listParameterRecords,
	);
	return data;
}

/**
 * Post User form
 * @param userModel user data to post
 * @returns IOrganizationResponse
 */
export async function saveUser(userModel: IUserModel): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		"/api/OrganizationManagement/SaveUser",
		userModel,
	);
	return data;
}

/**
 * Post Remote Associations
 * @param associations user remote associations to post
 * @returns IOrganizationResponse
 */
export async function saveUserRemoteAssociations(
	associations: IUserRemoteAssociations,
): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		"/api/OrganizationManagement/SaveUserRemoteAssociations",
		associations,
	);
	return data;
}

/**
 * Post Update user's organization
 * @param request user remote associations to post
 * @returns IOrganizationResponse
 */
export async function updateUserOrganization(
	request: IUserOrganizationUpdate,
): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		"/api/OrganizationManagement/UpdateUserOrganization",
		request,
	);
	return data;
}

/**
 * Post UserWeb activation
 * @param request userId
 * @returns IOrganizationResponse
 */
export async function sendUserWebActivation(
	userId: number,
	skipUserCheck?: boolean,
): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		`/api/OrganizationManagement/SendUserWebActivation?userId=${userId}&skipUserCheck=${skipUserCheck}`,
	);
	return data;
}

/**
 * Load Organization Enrollment page model
 * @returns IOrganizationEnrollmentModel
 */
export async function loadOrganizationEnrollment(id?: number): Promise<IOrganizationEnrollmentModel> {
	const data: IOrganizationEnrollmentModel = await api.get<IOrganizationEnrollmentModel>(
		`/api/OrganizationManagement/LoadEnrollment/${id}`,
	);
	return data;
}

/**
 * Post Organization enrollment form
 * @param orgModel organization enrollment data to post
 * @returns IOrganizationResponse
 */
export async function saveEnrollment(
	orgModel: IOrganizationEnrollmentFormModel,
): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		"/api/OrganizationManagement/SaveEnrollment",
		orgModel,
	);
	return data;
}

/**
 * Load Organization Agreements page model
 * @returns IOrganizationAgreementModel
 */
export async function loadOrganizationAgreement(id?: number): Promise<IOrganizationAgreementModel> {
	const data: IOrganizationAgreementModel = await api.get<IOrganizationAgreementModel>(
		`/api/OrganizationManagement/LoadAgreementData/${id}`,
	);
	return data;
}

/**
 * Load Organization Archiving page model
 * @returns IOrganizationArchivingModel
 */
export async function loadOrganizationArchiving(id?: number): Promise<IOrganizationArchivingModel> {
	const data: IOrganizationArchivingModel = await api.get(
		`/api/OrganizationManagement/LoadArchivingData/${id}`,
	);
	return data;
}

/**
 * Remove Archived Organization
 * @returns IOrganizationSaveResponse
 */
export async function removeArchivedOrganization(id?: number): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		`/api/OrganizationManagement/RemoveArchivedOrganization?id=${id}`,
	);
	return data;
}

/**
 * Add Archived Organizations
 * @returns IOrganizationSaveResponse
 */
export async function addArchivedOrganizations(
	model: IAddArchiveRequestModel,
): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		`/api/OrganizationManagement/AddArchivedOrganizations`,
		model,
	);
	return data;
}

/**
 * Load Archive Info for organization
 * @returns IOrganizationArchiveInfo
 */
export async function loadArchiveInfo(id?: number): Promise<IOrganizationArchiveInfo> {
	const data: IOrganizationArchiveInfo = await api.get<IOrganizationArchiveInfo>(
		`/api/OrganizationManagement/LoadArchiveInfo/${id}`,
	);
	return data;
}

/**
 * Archive the organization
 * @returns IOrganizationSaveResponse
 */
export async function archiveOrganization(
	model: IOrganizationArchiveRequest,
): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		`/api/OrganizationManagement/ArchiveOrganization`,
		model,
	);
	return data;
}

/**
 * Quick search organization and return top some results
 * @returns ISelectionOption[]
 */
export async function quickSearch(
	orgId: number,
	search: string,
	_callback: (options: SelectOptionType[]) => void,
): Promise<ISelectionOption[]> {
	const data: ISelectionOption[] = await api.get<ISelectionOption[]>(
		`/api/OrganizationManagement/QuickSearch?currentOrg=${orgId}&search=${search}`,
	);
	return data;
}

/**
 * Save default columns
 * @returns IOrganizationSaveResponse
 */
export async function saveDefaultColumns(
	model: ISaveDefaultColumnsRequest,
): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		`/api/OrganizationManagement/SaveDefaultColumns`,
		model,
	);
	return data;
}

/**
 * Post User Workflows
 * @param model user workflows request to post
 * @returns IOrganizationResponse
 */
export async function saveUserWorkflows(model: ISaveUserWorkflowRequest): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		"/api/OrganizationManagement/SaveUserWorkflows",
		model,
	);
	return data;
}

/**
 * Search for Epic user and return top some results
 * @returns ISelectionOption[]
 */
export async function epicUserSearch(
	search: string,
	_callback: (options: SelectOptionType[]) => void,
): Promise<SelectOptionType[]> {
	const data = await api.get<IFilterDataAsyncResponse>(
		`/api/OrganizationManagement/EpicUserSearch?search=${search}`,
	);
	return data.results;
}

/**
 * Post Research - Org Metadata fields
 * @param model research data to post
 * @returns IOrganizationResponse
 */
export async function saveResearch(model: ISaveResearchRequest): Promise<IOrganizationSaveResponse> {
	const data: IOrganizationSaveResponse = await api.post<IOrganizationSaveResponse>(
		"/api/OrganizationManagement/SaveResearch",
		model,
	);
	return data;
}

/**
 * Search for vendor organizations and return top some results
 * @returns ISelectionOption[]
 */
export async function vendorOrgSearch(
	orgId: number,
	search: string,
	_callback: (options: SelectOptionType[]) => void,
): Promise<SelectOptionType[]> {
	const data = await api.get<IFilterDataAsyncResponse>(
		`/api/OrganizationManagement/VendorOrgSearch?orgId=${orgId}&search=${search}`,
	);
	return data.results;
}
